import { useRef, useState } from 'react'
import { formatTime } from '../room/client'

interface Props {
  duration: number
  value: number
  /** Buffered ranges of the local player, seconds. */
  buffered: [number, number][]
  label: string
  onScrub: (pos: number | null) => void
  onCommit: (pos: number) => void
}

const KEY_STEP = 10
const KEY_COMMIT_DELAY = 500

/**
 * Seek bar with pointer capture, so a drag commits even when the pointer
 * is released outside the bar.
 */
export function SeekBar({ duration, value, buffered, label, onScrub, onCommit }: Props) {
  const ref = useRef<HTMLDivElement>(null)
  const dragging = useRef(false)
  const scrubbed = useRef<number | null>(null)
  const keyTimer = useRef(0)
  const [hover, setHover] = useState<number | null>(null)

  const pct = (t: number) => (duration > 0 ? Math.min(100, Math.max(0, (t / duration) * 100)) : 0)

  const posAt = (clientX: number) => {
    const r = ref.current!.getBoundingClientRect()
    const frac = Math.min(1, Math.max(0, (clientX - r.left) / r.width))
    return frac * duration
  }

  const scrub = (t: number) => {
    scrubbed.current = t
    onScrub(t)
  }

  const finish = (commit: boolean) => {
    dragging.current = false
    const t = scrubbed.current
    scrubbed.current = null
    onScrub(null)
    if (commit && t !== null) onCommit(t)
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    let t: number
    const base = scrubbed.current ?? value
    switch (e.key) {
      case 'ArrowLeft':
        t = base - KEY_STEP
        break
      case 'ArrowRight':
        t = base + KEY_STEP
        break
      case 'Home':
        t = 0
        break
      case 'End':
        t = duration
        break
      default:
        return
    }
    e.preventDefault()
    e.stopPropagation()
    scrub(Math.min(duration, Math.max(0, t)))
    // Repeated key presses add up into one seek.
    window.clearTimeout(keyTimer.current)
    keyTimer.current = window.setTimeout(() => finish(true), KEY_COMMIT_DELAY)
  }

  const disabled = duration <= 0
  return (
    <div
      ref={ref}
      className={`seek${disabled ? ' seek--disabled' : ''}`}
      role="slider"
      tabIndex={disabled ? -1 : 0}
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={Math.round(duration)}
      aria-valuenow={Math.round(value)}
      aria-valuetext={formatTime(value)}
      onKeyDown={onKeyDown}
      onPointerDown={(e) => {
        if (disabled || e.button !== 0) return
        try {
          e.currentTarget.setPointerCapture(e.pointerId)
        } catch {
          // Synthetic or already released pointers cannot be captured.
        }
        dragging.current = true
        scrub(posAt(e.clientX))
      }}
      onPointerMove={(e) => {
        if (disabled) return
        const t = posAt(e.clientX)
        setHover(t)
        if (dragging.current) scrub(t)
      }}
      onPointerUp={() => dragging.current && finish(true)}
      onPointerCancel={() => dragging.current && finish(false)}
      onLostPointerCapture={() => dragging.current && finish(true)}
      onPointerLeave={() => setHover(null)}
    >
      <div className="seek__track">
        {buffered.map(([s, e]) => (
          <span key={s} className="seek__buffered" style={{ left: `${pct(s)}%`, width: `${pct(e) - pct(s)}%` }} />
        ))}
        <span className="seek__played" style={{ width: `${pct(value)}%` }} />
      </div>
      <span className="seek__thumb" style={{ left: `${pct(value)}%` }} />
      {hover !== null && (
        <span className="seek__hover" style={{ left: `${pct(hover)}%` }}>
          {formatTime(hover)}
        </span>
      )}
    </div>
  )
}
